import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LoginIcon from "@mui/icons-material/Login";
import PersonIcon from "@mui/icons-material/Person";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { getOrgName, DEFAULT_ORG_NAME } from "../../services/backend/AppConfigService";
import { getEntityConfig } from "../../services/backend/EntityConfigService";

// Portada pública. Dos públicos muy distintos entran por la misma URL —una
// persona que se postula y una empresa que quiere firmar un convenio—, así que
// cada uno tiene su pestaña en vez de una lista de botones donde el otro no se
// reconoce.

const PERSON_STEPS = [
  {
    icon: <AssignmentIndIcon color="primary" />,
    title: "1. Regístrate",
    text: "Completa tus datos personales, formación y experiencia. Recibirás un correo con un enlace para retomar tu postulación cuando quieras.",
  },
  {
    icon: <UploadFileIcon color="primary" />,
    title: "2. Carga tus documentos",
    text: "Adjunta el acta de grado y los certificados de experiencia. Podrás completarlos en cualquier momento.",
  },
  {
    icon: <HowToRegIcon color="primary" />,
    title: "3. Selección y vinculación",
    text: "El equipo de Extensión revisa y valida tu documentación. Si eres seleccionado, te avisaremos para cargar los documentos restantes.",
  },
];

const ENTITY_STEPS = [
  {
    icon: <BusinessIcon color="primary" />,
    title: "1. Registra tu entidad",
    text: "Identifica la entidad, su representante legal y sus datos de contacto. Verificaremos el correo y el celular que registres.",
  },
  {
    icon: <DescriptionIcon color="primary" />,
    title: "2. Carga la documentación",
    text: "Los documentos que se te piden dependen de la naturaleza jurídica de la entidad. Los que tienen vigencia te los recordaremos antes de que se venzan.",
  },
  {
    icon: <HandshakeIcon color="primary" />,
    title: "3. Aprobación y convenios",
    text: "El equipo de Extensión revisa y aprueba el registro. A partir de ahí la entidad queda habilitada para suscribir convenios.",
  },
];

function StepCards({ steps }) {
  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {steps.map((s) => (
        <Grid item xs={12} md={4} key={s.title}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Stack spacing={1.5}>
                {s.icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.text}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function CallToAction({ title, subtitle, primary, secondary }) {
  return (
    <Card sx={{ bgcolor: "#f7f7f7" }}>
      <CardContent sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{subtitle}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
          {primary && (
            <Button size="large" variant="contained" startIcon={primary.icon} href={primary.href}>
              {primary.label}
            </Button>
          )}
          <Button size="large" variant="outlined" startIcon={secondary.icon} href={secondary.href}>
            {secondary.label}
          </Button>
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Button size="small" color="inherit" startIcon={<LoginIcon />} href="/login">
          ¿Eres funcionario? Iniciar sesión
        </Button>
      </CardContent>
    </Card>
  );
}

const Submission = () => {
  const [orgName, setOrgName] = useState(DEFAULT_ORG_NAME);
  const [tab, setTab] = useState("personas");
  // El interruptor sólo cierra los registros NUEVOS: quien ya está registrado
  // sigue entrando a actualizar sus documentos, porque apagar el portal no
  // debería dejar vencer los papeles de quien ya está dentro.
  const [entityRegistrationOpen, setEntityRegistrationOpen] = useState(true);

  useEffect(() => {
    getOrgName().then(setOrgName);
    getEntityConfig().then((cfg) => {
      setEntityRegistrationOpen(cfg.publicRegistrationEnabled !== false);
    });
  }, []);

  const isEntities = tab === "entidades";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          {orgName}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mt: 1 }}>
          Dirección de Extensión Académica
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 640, mx: "auto" }}>
          Regístrate una sola vez, mantén tu información al día y participa en las
          convocatorias y convenios de la Dirección de Extensión.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        centered
        sx={{ mb: 4, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="personas" icon={<PersonIcon />} iconPosition="start" label="Personas" />
        <Tab value="entidades" icon={<BusinessIcon />} iconPosition="start" label="Empresas y entidades" />
      </Tabs>

      {!isEntities && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}>
            Postulación y contratación de contratistas
          </Typography>
          <StepCards steps={PERSON_STEPS} />
          <CallToAction
            title="¿Listo para postularte?"
            subtitle="Inicia una nueva postulación o retoma una que ya empezaste."
            primary={{ label: "Postúlate", href: "/submission/new", icon: <AssignmentIndIcon /> }}
            secondary={{ label: "Continuar mi postulación", href: "/submission/continuar", icon: <RestartAltIcon /> }}
          />
        </>
      )}

      {isEntities && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}>
            Registro de empresas y entidades
          </Typography>
          <StepCards steps={ENTITY_STEPS} />
          <CallToAction
            title={entityRegistrationOpen ? "¿Listo para registrar tu entidad?" : "Registros temporalmente cerrados"}
            subtitle={
              entityRegistrationOpen
                ? "Registra una entidad nueva o entra a la ficha de una ya registrada para actualizar sus documentos."
                : "No estamos recibiendo registros nuevos en este momento. Si tu entidad ya está registrada, puedes entrar a actualizar sus documentos."
            }
            primary={
              entityRegistrationOpen
                ? { label: "Registrar mi entidad", href: "/submission/entidad/nueva", icon: <BusinessIcon /> }
                : null
            }
            secondary={{
              label: "Continuar mi registro",
              href: "/submission/entidad/continuar",
              icon: <RestartAltIcon />,
            }}
          />
        </>
      )}
    </Container>
  );
};

export default Submission;
